import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { TemplateSelector } from '../components/TemplateSelector';
import { API_BASE_URL } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';

const USER_ID_KEY = 'retrojet-user-id';

function getOrCreateUserId(): string {
  let userId = localStorage.getItem(USER_ID_KEY);
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem(USER_ID_KEY, userId);
  }
  return userId;
}

interface CreateBoardResponse {
  boardId?: string;
  id?: string;
  board?: { id: string; name: string };
}

class CreateBoardHttpError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'CreateBoardHttpError';
  }
}

const CREATE_BOARD_MAX_ATTEMPTS = 3;
const CREATE_BOARD_TIMEOUT_MS = 35000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientCreateError(error: unknown): boolean {
  if (error instanceof CreateBoardHttpError) {
    return error.status === undefined || [408, 429, 500, 502, 503, 504].includes(error.status);
  }
  return error instanceof TypeError || error instanceof DOMException;
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), CREATE_BOARD_TIMEOUT_MS);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function createBoardWithRetry(params: {
  boardId: string;
  name: string;
  templateId: string;
  adminId: string;
  authToken?: string;
}): Promise<CreateBoardResponse> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= CREATE_BOARD_MAX_ATTEMPTS; attempt++) {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-user-id': params.adminId,
        'x-idempotency-key': params.boardId,
      };
      if (params.authToken) {
        headers.Authorization = `Bearer ${params.authToken}`;
      }

      const response = await fetchWithTimeout(`${API_BASE_URL}/boards`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          boardId: params.boardId,
          idempotencyKey: params.boardId,
          name: params.name,
          templateId: params.templateId,
          template: params.templateId,
          adminId: params.adminId,
          userId: params.adminId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || errorData.error || `Failed to create board (${response.status})`;
        throw new CreateBoardHttpError(message, response.status);
      }

      return await response.json();
    } catch (error) {
      lastError = error;
      if (attempt === CREATE_BOARD_MAX_ATTEMPTS || !isTransientCreateError(error)) {
        throw error;
      }
      await delay(750 * 2 ** (attempt - 1));
    }
  }

  throw lastError instanceof Error ? lastError : new Error('Failed to create board');
}

export function CreateBoardPage(): JSX.Element {
  const navigate = useNavigate();
  const { user, sessionToken } = useAuth();
  const [boardName, setBoardName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!boardName.trim() || isCreating) return;

    setIsCreating(true);
    setError(null);

    try {
      const adminId = user?.id ?? getOrCreateUserId();
      const templateId = selectedTemplateId ?? 'start-stop-continue';
      const creationBoardId = crypto.randomUUID();
      const data = await createBoardWithRetry({
        boardId: creationBoardId,
        name: boardName.trim(),
        templateId,
        adminId,
        authToken: sessionToken ?? undefined,
      });
      const boardId = data.boardId || data.id || data.board?.id;
      if (!boardId) {
        throw new Error('Board created, but the response did not include a board ID');
      }
      navigate(`/board/${boardId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create board');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4.5rem)] flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Create New Board
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            Start a new team retrospective
          </p>
        </div>

        <form
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
          data-testid="create-board-form"
        >
          <div className="space-y-6">
            <div>
              <label htmlFor="boardName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Board Name
              </label>
              <input
                id="boardName"
                type="text"
                data-testid="board-name-input"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                placeholder="e.g., Sprint 23 Retrospective"
                required
                minLength={3}
                maxLength={100}
                disabled={isCreating}
                className="mt-1 input-field disabled:opacity-50"
              />
            </div>

            <TemplateSelector
              selectedTemplateId={selectedTemplateId}
              onTemplateSelect={setSelectedTemplateId}
            />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-board-submit"
              disabled={!boardName.trim() || isCreating}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Board'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
